import React, { useEffect, useRef } from 'react';
import Konva from 'konva';
import { Ellipse, Group, Line, Path, Rect, Text } from 'react-konva';
import { TGDComponent } from '../../../models/courses/graphicDesign';
import { resolveFontFamily } from '../core/fonts';
import { polygonPoints, toNodeTransform } from '../core/geometry';
import { getPictogram, PICTOGRAM_STROKE_WIDTH, PICTOGRAM_VIEWBOX } from '../core/pictograms';

interface GDNodeProps {
  component: TGDComponent; draggable: boolean; fontsVersion: number; onSelect: () => void;
  onPreview: (patch: Partial<TGDComponent>) => void;
  onCommit: (patch: Partial<TGDComponent>) => void;
  onDragMove: (node: Konva.Node) => void;
  onEdit?: (node: Konva.Text) => void;
}

const GDNode: React.FC<GDNodeProps> = ({ component: c, draggable, fontsVersion, onSelect,
  onPreview, onCommit, onDragMove, onEdit }) => {
  const textRef = useRef<Konva.Text>(null);
  const transform = toNodeTransform(c);
  const common = {
    id: c.id, x: transform.x, y: transform.y, rotation: transform.rotation,
    offsetX: c.width / 2, offsetY: c.height / 2, draggable, onClick: onSelect,
    onTap: onSelect, onDragMove: (event: Konva.KonvaEventObject<DragEvent>) => {
      onDragMove(event.target);
      onPreview({ x: event.target.x() - c.width / 2, y: event.target.y() - c.height / 2 });
    },
    onDragEnd: (event: Konva.KonvaEventObject<DragEvent>) => {
      onCommit({ x: event.target.x() - c.width / 2, y: event.target.y() - c.height / 2 });
    },
  };
  useEffect(() => {
    const node = textRef.current;
    if (node && Math.abs(node.height() - c.height) > 1) {
      onPreview({ height: node.height() });
    }
  }, [c.height, c.text, c.width, c.fontSize, c.fontPair, onPreview]);
  if (c.type === 'text') return (
    <Text {...common} key={`${c.id}-${fontsVersion}`} ref={textRef} text={c.text ?? ''} width={c.width}
      fontSize={c.fontSize ?? 16} fontFamily={resolveFontFamily(c)}
      fontStyle={String(c.fontWeight ?? 400)} align={c.align ?? 'left'}
      direction={c.direction ?? 'ltr'} fill={c.fill} lineHeight={1.15} wrap="word"
      onDblClick={event => onEdit?.(event.target as Konva.Text)}
      onDblTap={event => onEdit?.(event.target as Konva.Text)} />
  );
  if (c.type === 'pictogram') {
    const icon = getPictogram(c.icon);
    return <Group {...common}>
      <Rect width={c.width} height={c.height} fill="transparent" />
      {icon ? <Path data={icon.d} scaleX={c.width / PICTOGRAM_VIEWBOX}
        scaleY={c.height / PICTOGRAM_VIEWBOX} stroke={c.fill}
        strokeWidth={PICTOGRAM_STROKE_WIDTH} fillEnabled={false}
        lineCap="round" lineJoin="round" />
        : <Rect width={c.width} height={c.height} stroke={c.fill} dash={[6, 4]} />}
    </Group>;
  }
  const paint = { fill: c.fill, stroke: c.stroke, strokeWidth: c.strokeWidth ?? 0 };
  if (c.sides === 0) return <Ellipse {...common} {...paint} x={transform.x}
    y={transform.y} offsetX={0} offsetY={0}
    radiusX={c.width / 2} radiusY={c.height / 2} />;
  if (c.sides === 4) return <Rect {...common} {...paint}
    width={c.width} height={c.height} cornerRadius={c.cornerRadius ?? 0} />;
  return <Line {...common} {...paint} points={polygonPoints(c.sides ?? 3, c.width, c.height)} closed />;
};

export default GDNode;
